<template>
  <section class="page-shell employees-page">
    <div class="page-heading">
      <div>
        <h1>Equipe e acessos</h1>
        <p>Cadastre quem pode usar o workspace e defina o nível de acesso de cada pessoa.</p>
      </div>
      <v-btn color="primary" @click="openDialog()">
        <v-icon left small>mdi-account-plus-outline</v-icon>
        Nova pessoa
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
    <v-alert v-if="temporaryPassword" type="info" dense text>
      Senha temporária criada: <strong>{{ temporaryPassword }}</strong>
    </v-alert>

    <section class="access-summary">
      <article>
        <span>Usuários ativos</span>
        <strong>{{ activeCount }}</strong>
        <small>Pessoas com acesso liberado ao workspace</small>
      </article>
      <article>
        <span>Administradores</span>
        <strong>{{ adminCount }}</strong>
        <small>Podem gerenciar acessos e configurações</small>
      </article>
      <article>
        <span>Acessos suspensos</span>
        <strong>{{ inactiveCount }}</strong>
        <small>Usuários sem acesso operacional</small>
      </article>
    </section>

    <section class="data-panel">
      <div class="panel-title">
        <h2>Equipe cadastrada</h2>
        <span class="table-caption">O acesso é sempre vinculado ao workspace ativo.</span>
      </div>
      <v-simple-table>
        <thead>
          <tr>
            <th>Pessoa</th>
            <th>Papel de acesso</th>
            <th>Status</th>
            <th class="text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employee in employees" :key="employee.user_id">
            <td>
              <strong>{{ employee.name || '-' }}</strong>
              <div class="table-caption">{{ employee.email }}</div>
            </td>
            <td>{{ employee.role_name || 'Sem papel definido' }}</td>
            <td>
              <v-chip small :color="statusColor(employee.access_status)" text-color="white">
                {{ statusLabel(employee.access_status) }}
              </v-chip>
            </td>
            <td class="text-right">
              <v-btn icon small @click="openDialog(employee)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
              <v-btn icon small @click="removeAccess(employee)"><v-icon small>mdi-account-cancel-outline</v-icon></v-btn>
            </td>
          </tr>
          <tr v-if="!loading && !employees.length">
            <td colspan="4" class="empty-cell">Nenhuma pessoa cadastrada neste workspace.</td>
          </tr>
        </tbody>
      </v-simple-table>
    </section>

    <v-dialog v-model="dialog" max-width="620">
      <v-card>
        <v-card-title>{{ editing ? 'Editar acesso' : 'Nova pessoa' }}</v-card-title>
        <v-card-text class="team-dialog-body">
          <div class="team-dialog-grid">
            <v-text-field v-model="form.name" label="Nome completo" outlined dense hide-details />
            <v-text-field v-model="form.email" label="E-mail corporativo" outlined dense hide-details :disabled="Boolean(editing)" />
          </div>
          <div class="team-dialog-grid">
            <v-select v-model="form.role_id" :items="roleOptions" label="Papel de acesso" outlined dense hide-details />
            <v-select v-model="form.status" :items="statusOptions" label="Status" outlined dense hide-details />
          </div>
          <div class="access-hint">
            <v-icon>mdi-shield-account-outline</v-icon>
            <p>{{ selectedRoleHint }}</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Salvar acesso</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from '../../plugins/axios'
import { billingErrorMessage } from '../../utils/apiErrors'

const emptyForm = () => ({
  name: '',
  email: '',
  role_id: null,
  status: 'active'
})

export default {
  name: 'EmployeesPage',
  data () {
    return {
      employees: [],
      roles: [],
      dialog: false,
      editing: null,
      form: emptyForm(),
      loading: false,
      saving: false,
      error: '',
      temporaryPassword: '',
      statusOptions: [
        { text: 'Ativo', value: 'active' },
        { text: 'Suspenso', value: 'inactive' }
      ],
      roleHints: {
        tenant_owner: 'Acesso total ao workspace, conta, usuários, regras e evidências.',
        admin: 'Gerencia usuários, regras de uso, políticas e aprovações.',
        legal: 'Atua nas aprovações jurídicas, validações e evidências.',
        security_dpo: 'Acompanha dados pessoais, segurança, DPO e riscos sensíveis.',
        employee: 'Usa os fluxos de validação e consulta regras aprovadas.'
      }
    }
  },
  computed: {
    roleOptions () {
      return this.roles.map(role => ({ text: role.name, value: role.id }))
    },
    activeCount () {
      return this.employees.filter(employee => employee.access_status === 'active').length
    },
    inactiveCount () {
      return this.employees.filter(employee => employee.access_status !== 'active').length
    },
    adminCount () {
      return this.employees.filter(employee => ['tenant_owner', 'admin'].includes(employee.role_slug)).length
    },
    selectedRoleHint () {
      const role = this.roles.find(item => item.id === this.form.role_id)
      return role ? (this.roleHints[role.slug] || 'Define o nível de acesso desta pessoa no workspace.') : 'Selecione um papel para definir o nível de acesso.'
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.error = ''
      try {
        const [employees, roles] = await Promise.all([
          api.get('/employees'),
          api.get('/employees/roles')
        ])
        this.employees = employees.data
        this.roles = roles.data
      } catch (error) {
        this.error = billingErrorMessage(error, 'Não foi possível carregar a equipe.')
      } finally {
        this.loading = false
      }
    },
    openDialog (employee) {
      this.temporaryPassword = ''
      this.editing = employee || null
      this.form = employee
        ? {
            name: employee.name,
            email: employee.email,
            role_id: employee.role_id,
            status: employee.access_status
          }
        : { ...emptyForm(), role_id: this.defaultRoleId() }
      this.dialog = true
    },
    defaultRoleId () {
      const employeeRole = this.roles.find(role => role.slug === 'employee')
      return employeeRole ? employeeRole.id : (this.roles[0] ? this.roles[0].id : null)
    },
    async save () {
      this.saving = true
      this.error = ''
      this.temporaryPassword = ''
      try {
        if (this.editing) {
          await api.put(`/employees/${this.editing.user_id}`, this.form)
        } else {
          const { data } = await api.post('/employees', this.form)
          this.temporaryPassword = data.temporary_password || ''
        }
        this.dialog = false
        await this.load()
      } catch (error) {
        this.error = error.response && error.response.data ? error.response.data.message : 'Não foi possível salvar o acesso.'
      } finally {
        this.saving = false
      }
    },
    async removeAccess (employee) {
      await api.delete(`/employees/${employee.user_id}`)
      await this.load()
    },
    statusLabel (status) {
      return { active: 'Ativo', inactive: 'Suspenso', pending: 'Pendente' }[status] || status
    },
    statusColor (status) {
      return { active: 'success', inactive: 'grey', pending: 'warning' }[status] || 'grey'
    }
  }
}
</script>

<style lang="scss" scoped>
.team-dialog-body {
  display: grid;
  gap: 16px;
  padding-top: 8px !important;
}

.team-dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.access-hint {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border: 1px solid #dfe8e4;
  background: #f8fbfa;
}

.access-hint .v-icon {
  color: #0f4efb;
}

.access-hint p {
  margin: 0;
  color: #60717d;
  line-height: 1.45;
}

@media (max-width: 760px) {
  .team-dialog-grid {
    grid-template-columns: 1fr;
  }
}
</style>
